<template>
  <div class="linked-tab">
    <div class="section-card">
      <div class="section-header section-header-flex">
        <div>
          <h2>Linked Properties</h2>
          <p class="section-subtitle">
            Group properties together to send one combined report to a single owner.
          </p>
        </div>
        <button class="btn btn-primary" @click="showCreateForm = !showCreateForm">
          <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="8" y1="2" x2="8" y2="14"/>
            <line x1="2" y1="8" x2="14" y2="8"/>
          </svg>
          Create Group
        </button>
      </div>

      <div class="info-banner">
        Use groups when one owner has multiple properties and you want to send a single combined report.
        Each property still tracks bookings and commission individually — shared costs like cleaning and
        laundry are set at the group level and split across the report automatically.
      </div>

      <!-- Loading / Error states -->
      <div v-if="loading" class="empty-line">Loading groups…</div>
      <div v-if="globalError" class="error-banner">{{ globalError }}</div>

      <!-- Existing groups -->
      <div v-for="group in groups" :key="group.id" class="group-wrapper">
        <GroupCard
          :group="group"
          :available-properties="availableProperties"
          @update="onUpdateGroup"
          @delete="onDeleteGroup"
          @add-member="onAddMember"
          @remove-member="onRemoveMember"
        />
      </div>

      <!-- Empty state -->
      <div v-if="!loading && groups.length === 0 && !showCreateForm" class="empty-state">
        <div class="empty-state-icon">
          <svg viewBox="0 0 16 16" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M2 13V6l6-4 6 4v7"/>
            <path d="M6 13v-4h4v4"/>
          </svg>
        </div>
        <div class="empty-state-title">No linked groups yet</div>
        <div class="empty-state-sub">Create your first group to combine multiple properties under one owner.</div>
      </div>

      <!-- Create group form -->
      <div v-if="showCreateForm" class="create-form">
        <div class="create-form-title">New linked properties group</div>

        <div class="field" style="margin-bottom: 14px;">
          <label class="field-label">Group name</label>
          <input
            v-model="newGroup.name"
            type="text"
            placeholder="e.g. Plymouth Harbour Block"
            class="setting-input"
          />
          <div class="field-hint">A name to identify this linked set of properties.</div>
        </div>

        <div class="owner-warning">
          Owner details are pulled automatically from the selected properties.
          All properties in a linked group must belong to the same owner.
        </div>

        <div class="field" style="margin-bottom: 14px;">
          <label class="field-label">Select properties to include</label>
          <div class="props-checkbox-list">
            <label
              v-for="p in availableProperties"
              :key="p.id"
              class="prop-checkbox-row"
              :class="{ checked: newGroup.initial_property_ids.includes(p.id) }"
            >
              <input
                type="checkbox"
                :value="p.id"
                v-model="newGroup.initial_property_ids"
              />
              <span class="prop-checkbox-name">{{ p.name }}</span>
            </label>
            <div v-if="availableProperties.length === 0" class="empty-line">
              No properties available — check your PMS configuration.
            </div>
          </div>
          <div class="field-hint">
            {{ newGroup.initial_property_ids.length }} selected.
            You can add or remove properties later from the group's Edit panel.
          </div>
        </div>

        <div v-if="createError" class="error-banner">{{ createError }}</div>

        <div class="btn-row-actions">
          <button class="btn btn-outline" @click="cancelCreate">Cancel</button>
          <button class="btn btn-primary" :disabled="!canCreate || creating" @click="onCreateGroup">
            {{ creating ? 'Creating…' : 'Create group' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== Quick test panel ===== -->
    <!-- Temporary tool to validate the combined-report endpoint end-to-end.
         Will be replaced by the Reservations page UI in W2.1. -->
    <div class="section-card test-panel" v-if="groups.length > 0">
      <div class="section-header">
        <h2>Test combined report</h2>
        <p class="section-subtitle">
          Sends the combined report for the current month using the same call the Reservations page
          will make. Pick a group and the email to receive the test. This panel will move to
          Reservations later.
        </p>
      </div>

      <div class="test-fields">
        <div class="field">
          <label class="field-label">Group</label>
          <select v-model="testForm.groupId" class="setting-input">
            <option value="" disabled>Choose a group…</option>
            <option v-for="g in groups" :key="g.id" :value="g.id">
              {{ g.name }} ({{ g.member_count || (g.memberships || []).length }} properties)
            </option>
          </select>
        </div>

        <div class="field">
          <label class="field-label">Send to email</label>
          <input
            v-model="testForm.toEmail"
            type="email"
            placeholder="your-test@example.com"
            class="setting-input"
          />
          <div class="field-hint">Overrides the group's owner email for this test only.</div>
        </div>

        <div class="field">
          <label class="field-label">Year</label>
          <input
            v-model.number="testForm.year"
            type="number"
            min="2020"
            max="2099"
            class="setting-input"
          />
        </div>

        <div class="field">
          <label class="field-label">Month</label>
          <select v-model.number="testForm.month" class="setting-input">
            <option v-for="m in 12" :key="m" :value="m">
              {{ String(m).padStart(2, '0') }} — {{ monthLabel(m) }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="testError" class="error-banner">{{ testError }}</div>
      <div v-if="testSuccess" class="success-banner">{{ testSuccess }}</div>

      <div class="btn-row-actions">
        <button
          class="btn btn-primary"
          :disabled="!canSendTest || sendingTest"
          @click="onSendTestReport"
        >
          {{ sendingTest ? 'Sending…' : 'Send test report' }}
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import GroupCard from './GroupCard.vue'
import propertyGroupsApi from '@/services/propertyGroups'
import { SETTINGS } from '@/config/settings'
import axios from 'axios'

export default {
  name: 'LinkedPropertiesTab',
  components: { GroupCard },
  data() {
    return {
      groups: [],
      availableProperties: [],
      loading: false,
      globalError: '',

      showCreateForm: false,
      creating: false,
      createError: '',
      newGroup: { name: '', initial_property_ids: [] },

      // Quick test panel (W1.4) — to be removed when Reservations UI lands in W2.1
      testForm: {
        groupId: '',
        toEmail: '',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      },
      sendingTest: false,
      testError: '',
      testSuccess: '',
    }
  },
  computed: {
    canCreate() {
      return this.newGroup.name.trim().length > 0 && this.newGroup.initial_property_ids.length > 0
    },
    canSendTest() {
      const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.testForm.toEmail.trim())
      const y = Number(this.testForm.year)
      const m = Number(this.testForm.month)
      const periodOk = Number.isInteger(y) && y >= 2020 && y <= 2099 && Number.isInteger(m) && m >= 1 && m <= 12
      return Boolean(this.testForm.groupId) && emailLooksValid && periodOk
    },
  },
  async mounted() {
    await Promise.all([this.fetchGroups(), this.fetchProperties()])
  },
  methods: {
    async fetchGroups() {
      this.loading = true
      this.globalError = ''
      try {
        this.groups = await propertyGroupsApi.list()
      } catch (e) {
        this.globalError = this.formatError(e, 'Failed to load groups.')
      } finally {
        this.loading = false
      }
    },
    async fetchProperties() {
      // Mirror SettingsView.fetchProperties() — paginated pms-configs, then
      // properties under {status, properties: {data: [...]}}.
      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }
        const cfgResp = await axios.get(
          `${SETTINGS.API.baseUrl}/api/users/pms-configs/`,
          { headers },
        )
        const configs = cfgResp.data.results || cfgResp.data
        if (!Array.isArray(configs) || configs.length === 0) {
          this.availableProperties = []
          return
        }
        const propsResp = await axios.get(
          `${SETTINGS.API.baseUrl}/api/pms/properties/?pms_config_id=${configs[0].id}`,
          { headers },
        )
        let list = []
        if (propsResp.data && propsResp.data.status === 'success') {
          list = propsResp.data.properties.data || []
        } else if (Array.isArray(propsResp.data)) {
          list = propsResp.data
        }
        this.availableProperties = list.map((p) => ({
          id: String(p.id),
          name: p.name || `Property ${p.id}`,
        }))
      } catch (e) {
        // Non-blocking — group creation will still work without the dropdown
        console.warn('Failed to fetch properties for group dropdown', e)
      }
    },
    cancelCreate() {
      this.showCreateForm = false
      this.newGroup = { name: '', initial_property_ids: [] }
      this.createError = ''
    },
    async onCreateGroup() {
      if (!this.canCreate) return
      this.creating = true
      this.createError = ''
      let createdGroup = null
      const failed = []
      try {
        createdGroup = await propertyGroupsApi.create({
          name: this.newGroup.name.trim(),
        })
        // Add each selected property. Stop on the first owner-mismatch / dup error
        // and roll back the whole group, since partial state is confusing.
        for (const pid of this.newGroup.initial_property_ids) {
          try {
            await propertyGroupsApi.addMember(createdGroup.id, pid)
          } catch (err) {
            failed.push({ pid, err })
            break
          }
        }
        if (failed.length) {
          // Roll back: delete the group entirely so the user can retry cleanly.
          try { await propertyGroupsApi.remove(createdGroup.id) } catch (_) { /* swallow */ }
          const propName = this.availableProperties.find((p) => p.id === failed[0].pid)?.name || failed[0].pid
          this.createError = `Could not add "${propName}": ${this.formatError(failed[0].err, 'unknown error')}`
          return
        }
        await this.fetchGroups()
        this.cancelCreate()
      } catch (e) {
        this.createError = this.formatError(e, 'Could not create group.')
        if (createdGroup) {
          try { await propertyGroupsApi.remove(createdGroup.id) } catch (_) { /* swallow */ }
        }
      } finally {
        this.creating = false
      }
    },
    async onUpdateGroup(groupId, payload, callbacks) {
      try {
        await propertyGroupsApi.update(groupId, payload)
        await this.fetchGroups()
        callbacks?.resolve?.()
      } catch (e) {
        callbacks?.reject?.(e)
      }
    },
    async onDeleteGroup(groupId) {
      this.globalError = ''
      try {
        await propertyGroupsApi.remove(groupId)
        await this.fetchGroups()
      } catch (e) {
        this.globalError = this.formatError(e, 'Failed to delete group.')
      }
    },
    async onAddMember(groupId, propertyId, callbacks) {
      try {
        await propertyGroupsApi.addMember(groupId, propertyId)
        await this.fetchGroups()
        callbacks?.resolve?.()
      } catch (e) {
        callbacks?.reject?.(e)
      }
    },
    async onRemoveMember(groupId, propertyId) {
      this.globalError = ''
      try {
        await propertyGroupsApi.removeMember(groupId, propertyId)
        await this.fetchGroups()
      } catch (e) {
        this.globalError = this.formatError(e, 'Failed to remove member.')
      }
    },
    formatError(err, fallback) {
      if (err?.response?.data?.detail) return err.response.data.detail
      if (err?.response?.status === 401) return 'Session expired. Please log in again.'
      if (err?.message) return err.message
      return fallback || 'Something went wrong.'
    },

    // --- Quick test panel ---
    // Sends the exact same call Reservations will use in W2.1: current month,
    // group's split & format, owner email defaulted (overridden by the test email).
    async onSendTestReport() {
      if (!this.canSendTest || this.sendingTest) return
      this.sendingTest = true
      this.testError = ''
      this.testSuccess = ''
      try {
        const result = await propertyGroupsApi.generateReport(this.testForm.groupId, {
          year: Number(this.testForm.year),
          month: Number(this.testForm.month),
          send_email: true,
          to_email: this.testForm.toEmail.trim(),
        })
        const recipient = result?.sent_to || this.testForm.toEmail.trim()
        this.testSuccess = `Report sent to ${recipient} (file: ${result?.filename || 'PDF'}).`
      } catch (e) {
        this.testError = this.formatError(e, 'Failed to send report.')
      } finally {
        this.sendingTest = false
      }
    },
    monthLabel(m) {
      return ['January','February','March','April','May','June','July','August','September','October','November','December'][m - 1] || ''
    },
  },
}
</script>

<style scoped>
.linked-tab {
  width: 100%;
}

.section-header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.section-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 4px 0 0 0;
}

.info-banner {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 16px 0 20px 0;
  font-size: 12px;
  color: #4338ca;
  line-height: 1.6;
}

.group-wrapper {
  margin-bottom: 12px;
}

.create-form {
  border: 1.5px dashed #c7d2fe;
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
  background: #f5f7ff;
}
.create-form-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
}

/* Quick test panel — visually distinct from the main config sections */
.test-panel {
  margin-top: 20px;
  background: #fafafa;
  border-left: 4px solid #f59e0b;
}
.test-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 16px 0;
}
.test-fields .field {
  display: flex;
  flex-direction: column;
}
.test-fields .field-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}
.test-fields .field-hint {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}
.success-banner {
  margin-top: 10px;
  padding: 10px 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  border-radius: 8px;
  font-size: 13px;
}
@media (max-width: 720px) {
  .test-fields { grid-template-columns: 1fr; }
}

.field {
  display: flex;
  flex-direction: column;
}
.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}
.field-hint {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  line-height: 1.4;
}

.owner-warning {
  font-size: 12px;
  color: #6b7280;
  background: white;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
  line-height: 1.6;
  border: 1px solid #e5e7eb;
}

.btn-row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}
.empty-state-icon {
  display: flex;
  justify-content: center;
  color: #c7d2fe;
  margin-bottom: 10px;
}
.empty-state-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}
.empty-state-sub {
  font-size: 13px;
  color: #6b7280;
}
.empty-line {
  font-size: 13px;
  color: #6b7280;
  padding: 16px 0;
  text-align: center;
}

.error-banner {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 8px;
  font-size: 13px;
}

/* Mirror SettingsView button styles for visual consistency */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-primary {
  background: #667eea;
  color: white;
  border-color: #667eea;
}
.btn-primary:hover:not(:disabled) {
  background: #5a67d8;
}
.btn-outline {
  background: transparent;
  color: #667eea;
  border-color: #667eea;
}
.btn-outline:hover:not(:disabled) {
  background: #667eea;
  color: white;
}

.setting-input {
  padding: 10px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #1f2937;
  background: white;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}
.setting-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Multi-select checkbox list (group creation) */
.props-checkbox-list {
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
}
.prop-checkbox-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.prop-checkbox-row:hover {
  background: #f5f7ff;
}
.prop-checkbox-row.checked {
  background: #eef2ff;
}
.prop-checkbox-row input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #667eea;
  cursor: pointer;
  margin: 0;
}
.prop-checkbox-name {
  font-size: 13px;
  color: #1f2937;
}

/* Reuse SettingsView card style */
.section-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}
.section-header h2 {
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}

@media (max-width: 768px) {
  .section-card { padding: 16px !important; }
  .section-header { flex-wrap: wrap; gap: 8px; }
  .section-header h2 { font-size: 16px; }
  .modal, .modal-content, .modal-container {
    max-width: 96vw !important;
    width: 96vw !important;
    max-height: 92vh;
  }
}

@media (max-width: 480px) {
  .modal, .modal-content, .modal-container {
    max-width: 100vw !important;
    width: 100vw !important;
    max-height: 100vh;
    border-radius: 0 !important;
  }
}
</style>
